import type {
  CampusResponse,
  CatalogEntryResponse,
  CreatePastorRequest,
  CreateThemeRequest,
  PastorResponse,
  TenantResponse,
  ThemeResponse,
  UpdatePastorRequest,
  UpdateTenantProfileRequest,
  UpdateThemeRequest,
} from '@tabita/contracts';
import { apiRequest } from './api-client';

export const tenancyApi = {
  getCurrent() {
    return apiRequest<TenantResponse>('/tenancy/current');
  },

  listCampuses(signal?: AbortSignal) {
    return apiRequest<readonly CampusResponse[]>('/tenancy/campuses', { signal });
  },

  updateProfile(profile: UpdateTenantProfileRequest) {
    return apiRequest<TenantResponse>('/tenancy/current', { method: 'PATCH', body: profile });
  },

  /** Active entries only — the profile form's picker, open to any member. */
  listDenominations() {
    return apiRequest<readonly CatalogEntryResponse[]>('/tenancy/denominations');
  },

  listCouncils() {
    return apiRequest<readonly CatalogEntryResponse[]>('/tenancy/councils');
  },

  listThemes() {
    return apiRequest<readonly ThemeResponse[]>('/tenancy/themes');
  },

  getActiveTheme(signal?: AbortSignal) {
    return apiRequest<ThemeResponse>('/tenancy/themes/active', { signal });
  },

  createTheme(body: CreateThemeRequest) {
    return apiRequest<ThemeResponse>('/tenancy/themes', { method: 'POST', body });
  },

  updateTheme(id: string, body: UpdateThemeRequest) {
    return apiRequest<ThemeResponse>(`/tenancy/themes/${id}`, { method: 'PATCH', body });
  },

  activateTheme(id: string) {
    return apiRequest<ThemeResponse>(`/tenancy/themes/${id}/activate`, { method: 'POST' });
  },

  deleteTheme(id: string) {
    return apiRequest<{ deleted: true }>(`/tenancy/themes/${id}`, { method: 'DELETE' });
  },

  listPastors(includeArchived = false, signal?: AbortSignal) {
    return apiRequest<readonly PastorResponse[]>('/tenancy/pastors', {
      query: { includeArchived },
      signal,
    });
  },

  createPastor(body: CreatePastorRequest) {
    return apiRequest<PastorResponse>('/tenancy/pastors', { method: 'POST', body });
  },

  updatePastor(id: string, body: UpdatePastorRequest) {
    return apiRequest<PastorResponse>(`/tenancy/pastors/${id}`, { method: 'PATCH', body });
  },

  archivePastor(id: string) {
    return apiRequest<{ archived: true }>(`/tenancy/pastors/${id}/archive`, { method: 'POST' });
  },

  restorePastor(id: string) {
    return apiRequest<{ restored: true }>(`/tenancy/pastors/${id}/restore`, { method: 'POST' });
  },
};
